import { IClinicMember, MembershipStatus } from "./models/clinic";
import ResourcesIds from "../resources/ResourcesIds";
import { UserRole } from "../types";

/**
 * How a clinic membership is labelled on screen.
 *
 * Shared by the platform owner's member list and a clinic admin's own console: the two
 * show the same four statuses, and a status that reads one way in one place and another
 * way in the other would be a bug rather than a style choice.
 */
export const STATUS_LABELS: Record<MembershipStatus, string> = {
    ACTIVE: ResourcesIds.MEMBER_STATUS_ACTIVE,
    PENDING: ResourcesIds.MEMBER_STATUS_PENDING,
    INVITATION_EXPIRED: ResourcesIds.MEMBER_STATUS_EXPIRED,
    REVOKED: ResourcesIds.MEMBER_STATUS_REVOKED,
};

/** Warning rather than default for a lapsed invitation: it is the one to act on. */
export const STATUS_COLOURS: Record<MembershipStatus, 'success' | 'info' | 'warning' | 'default'> = {
    ACTIVE: 'success',
    PENDING: 'info',
    INVITATION_EXPIRED: 'warning',
    REVOKED: 'default',
};

/**
 * Whose invitation can be sent again.
 *
 * A pending one is the case this exists for: "they never got the email", or lost it. A
 * patient's link has no expiry and sending again repeats the same email with the same
 * link, so it costs nothing and never strands the copy they already have.
 *
 * INVITATION_EXPIRED stays in the list although a patient no longer reaches it -- their
 * links stopped lapsing. The status still exists for staff, whose links last a day, and a
 * patient who somehow reads that way is exactly somebody to send an invitation to.
 *
 * REVOKED is not here. Somebody whose access was taken away is readmitted by an admin,
 * and quietly reinstating them from a therapist's list would be a different decision
 * wearing the same button.
 *
 * ACTIVE is not here either, and that one is load-bearing rather than a policy: the server
 * treats re-inviting somebody who already works here as a no-op and still answers 201, so
 * an ACTIVE chip that offered this would report a sent invitation that was never sent.
 */
export const RESENDABLE_STATUSES: MembershipStatus[] = ['INVITATION_EXPIRED', 'PENDING'];

/**
 * Whose invitation can be taken back: one nobody has accepted yet.
 *
 * The same two as above, and for the mirror-image reason. A patient's link never lapses,
 * so an invitation that went to the wrong address stays good until it is withdrawn, and
 * this is the only thing that ends it. Once somebody has accepted there is access to
 * remove rather than an invitation to withdraw, which is an administrator's decision and a
 * different endpoint -- the API refuses this one for an ACTIVE patient.
 */
export const WITHDRAWABLE_STATUSES: MembershipStatus[] = ['INVITATION_EXPIRED', 'PENDING'];

export const ROLE_LABELS: Record<UserRole, string> = {
    SUPER_ADMIN: ResourcesIds.ROLE_SUPER_ADMIN,
    ADMIN: ResourcesIds.ROLE_ADMIN,
    THERAPIST: ResourcesIds.ROLE_THERAPIST,
    CUSTOMER: ResourcesIds.ROLE_CUSTOMER,
};

/**
 * Either name may be missing until the person claims their account.
 *
 * Typed on the two fields rather than on IClinicMember so it also names a therapist
 * attached to a patient, who is the same person written down more briefly -- and now
 * anything else the app draws as a person, since `IPerson` satisfies this too.
 *
 * Null as well as undefined, because that is what actually arrives: an unclaimed account has
 * `null` in both columns and Jackson sends it as `null`. The filter already coped; only the
 * type was narrower than the behaviour.
 */
export const memberFullName = (
    member: { firstName?: string | null; lastName?: string | null }
): string => [member.firstName, member.lastName].filter(Boolean).join(' ');
